'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import TasksTabById from 'src/parts/tabs/TasksTabById';
import { getUserTasks } from 'src/api/admin/tasks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/users/$userId/tasks/')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
			filter: (search.filter as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const { userId } = params;
		const tasks = await getUserTasks(userId, deps);
		return { tasks };
	},
	component: TasksTab,
});

function TasksTab() {
	const { userId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const searchParams = Route.useSearch();
	const { tasks: initialTasks } = Route.useLoaderData();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();

	const { data: tasks, refetch: refetchTasks } = useQuery({
		queryKey: ['user-tasks', userId, searchParams],
		queryFn: () => getUserTasks(userId, searchParams),
		initialData: initialTasks,
	});

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-roles-with-tasks', userId] });
		refetchTasks();
	};

	const handleReload = () => {
		refetchTasks();
	};

	const handleTaskSelected = (taskId: string) => {
		navigate({ to: `/users/$userId/tasks/$taskId`, params: { userId, taskId }, search: { userId } });
	};

	return (
		<TasksTabById
			tasks={tasks}
			authenticatedUser={authenticatedUser}
			onUpdate={handleUpdate}
			onReload={handleReload}
			onTaskSelected={handleTaskSelected}
		/>
	);
}
